import { IWinRatioConfig } from '@nolimitcity/slot-game/bin/utils/server/IData';
import { Presentation } from '../../../core/gameflow/controller/Presentation';
import { StateHandler } from '../../../core/utils/StateHandler';
import { WinGameData } from '../../server/data/GameDataModels';
import { GameConfig } from '../../config/base/GameConfig';
import { SoundManager } from '../../config/sound/SoundManager';
import { SlotGame } from '@nolimitcity/slot-game/bin/SlotGame';
import { GameMode } from '@nolimitcity/slot-game/bin/flowcontrol/GameMode';
import { GameData } from '../../server/data/GameData';
import { BeheadedEngineManager } from '../../reels/BeheadedEngineManager';
import { BeheadedSymbol } from '../../reels/symbol/BeheadedSymbol';
import { Beheaded } from '../../Beheaded';
import { BeheadedSound } from '../../sound/BeheadedSound';
import { AnimationEffects } from '../../../core/effects/AnimationEffects';
import { Power2 } from 'gsap';
import { MathHelper } from '../../../core/utils/MathHelper';
import { Logger } from '@nolimitcity/slot-launcher/bin/utils/Logger';
import { SoundConfig } from '../../config/sound/SoundConfig';
import { SoulWild } from '../feature/SoulWild';

class BigWinStates extends StateHandler {
    public static readonly START: string = 'start';
    public static readonly SHOW: string = 'show';
    public static readonly STOP: string = 'stop';
    public static readonly DONE: string = 'done';
}

export class BigWinPresentation extends Presentation {
    private _winRatios: IWinRatioConfig[];
    private _SFSwinRatios: IWinRatioConfig[];

    private targetRatio:IWinRatioConfig[];

    private _winData: WinGameData;

    private _winLevelTarget: number = 0;
    private _currLevel: number = 0;

    private _maxBigWinLevels: number = 4;

    private _bounceDuration: number = 0.25;
    private _bigWinStates: BigWinStates = new BigWinStates();

    private isTotalWin: boolean = false;
    private isBelowStake: boolean = false;

    private currentLevel: number;

    private swPosMap: boolean[][];
    private swirlPlayed: boolean = false;
    private swSize: number;

    private levelSoundPlaying: boolean = true;

    initialization(): void {
        this._winRatios = GameConfig.WIN_RATIOS.bigWin;
        this._SFSwinRatios = GameConfig.WIN_RATIOS.SFSBigWin;
    }


    public setGamedData(data: GameData): void {
        this._winData = data.Win;

        this.isBelowStake = data.Win.isWinBelowStake!();

        this.isTotalWin = data.mode !== GameMode.NORMAL && data.Win.totalWin != data.Win.singleWin;
        this.targetRatio = data.currentLevel === 5 ? this._SFSwinRatios : this._winRatios;
        this._winLevelTarget = this.targetRatio.findIndex((indx) => indx.ratio > this._winData.winRatio*.9 ) - 1;
        if (this._winLevelTarget < 0) this._winLevelTarget = this.targetRatio.length - 1;

        this.currentLevel = data.currentLevel;

        this.swPosMap = data.Reel.SWBoolMap;
        this.swSize = data.Reel.SWSize;
    }

    preStart(): void {
        this._currLevel = 0;
        this._bigWinStates.set(BigWinStates.START);
        this.swirlPlayed = false;
    }

    public checkForActivation(data: GameData): boolean {
        return data.Win.isBigWin;
    }

    public createPresentation(enableSkip: Function): void {
        enableSkip(this._bigWinStates.equal(BigWinStates.SHOW));
        switch (this._bigWinStates.value) {
            case BigWinStates.START:
                this.preWin();
                break;
            case BigWinStates.SHOW:
                this.playBigWinLevelAnimation();
                break;
            case BigWinStates.STOP:
                this.postWin();
                break;
            case BigWinStates.DONE:
                this.PresentationState.setInactive();
                break;
            default:
                break;
        }
    }

    private preWin(): void {
        Beheaded.soundPlayer.playSound(BeheadedSound.BIG_WIN_INTRO);
        let delay: number = 0;
        const countDurr: number = 1.172;
        let playSW: boolean = true;

        for (let reel = 0; reel < this._winData.allBetWayWinData.reelWinPositions.length; reel++) {
            for (let row = 0; row < this._winData.allBetWayWinData.reelWinPositions[reel].length; row++) {
                const sym: BeheadedSymbol | undefined = BeheadedEngineManager.getReel(reel).getSymbol(row);
                const hits: boolean = this._winData.allBetWayWinData.reelWinPositions[reel][row];
                if (sym && !sym.isScatter && hits) {
                    if (this.swPosMap[reel][row] && playSW) {
                        this.timeline.add(() => {
                            Beheaded.souldWildController.showWin(1, this.swSize);
                        }, delay);
                        this.swirlPlayed = true;
                        playSW = false;
                    }
                    this.timeline.add(() => {
                        sym.playWin(false, 0.02, true);
                    }, delay);
                }
            }
        }

        this.timeline.add(() => {
            Beheaded.backgroundCharacter.showSmokeLevelUp(this.currentLevel, false);
        }, 0);

        this.timeline.add(Beheaded.counter.play(0, this.betValueRatio(0), countDurr, undefined));

        this.timeline.add(() => {
            Beheaded.counter.moveDown(false, 0.5);
        }, '-=0.5');

        SoundManager.fadeOutAmbient(2000, 0);

        this.timeline.add(() => {
            Beheaded.counter.changeToBigWinFont(true);
        });

        const crasyIdea: number = this.timeline.duration();
        this.timeline.to(Beheaded.reelEngineManager.parent.scale, { x: 1.025, y: 1.025, duration: crasyIdea / 2 }, 0);
        this.timeline.to(
            Beheaded.reelEngineManager.parent.scale,
            { x: 1, y: 1, duration: crasyIdea / 2, ease: 'Back.easeIn' },
            crasyIdea / 2
        );

        AnimationEffects.Shake({
            x: 1.15,
            y: 1.15,
            obj: Beheaded.reelEngineManager.parent,
            hertz: 60,
            duration: crasyIdea,
            updateOnComplete: true,
            positionCb: () => new PIXI.Point(360, 360), //new PIXI.Point(0,300),
            Intensity: 2.75,
            ease: Power2.easeOut,
        });

        this._bigWinStates.set(BigWinStates.SHOW);
    }

    public playBigWinLevelAnimation(): void {

        if (this.levelSoundPlaying) {
            Beheaded.backgroundCharacter.fadeLevelSoundOnBigWin(this.currentLevel, true);
            Beheaded.soundPlayer.fadeSound(BeheadedSound.MAIN_GAME_MUSIC, 1, 0, 200);
            if(this.swirlPlayed)
                Beheaded.soundPlayer.fadeSound(SoundConfig.WILD_FIRE_LOOP, SoulWild.volumeFire, 0, 200);
            this.levelSoundPlaying = false;
        }

        const presLvl: number = Math.min(this._currLevel, this._winLevelTarget);
        let bounceDurr: number = Beheaded.counter.bounceHit(8, this._bounceDuration, false, true).duration();
        const bigWinLevel: number = Math.min(presLvl, this._maxBigWinLevels);

        Beheaded.soundPlayer.stopSound(BeheadedSound.BIG_WIN[MathHelper.clamp(presLvl - 1, 0, 4)]);
        Beheaded.soundPlayer.playSound(BeheadedSound.BIG_WIN[MathHelper.clamp(presLvl, 0, 4)]);

        let countUpDurr: number = this.targetRatio[presLvl].duration; // Beheaded.poster.bigWinPresentationDuration(bigWinLevel);

        if (reachedLastLevel(presLvl)) {
            this.timeline.add(Beheaded.counter.moveToReset(countUpDurr, false));
        }

        if(presLvl == 3) Beheaded.parti.triggerFlames(true);

        this.timeline.add(this.playBigWinCountUp(presLvl, Math.abs(countUpDurr - bounceDurr)), bounceDurr);

        this.timeline.add(Beheaded.poster.playBigWin(bigWinLevel, countUpDurr), 0);

        if (presLvl >= this._winLevelTarget) {
            // When it Ends
            this.timeline.add(() => {
                Beheaded.soundPlayer.fadeSound(BeheadedSound.BIG_WIN[bigWinLevel], 1, 0, 500);
                Beheaded.soundPlayer.playSound(BeheadedSound.BIG_WIN_END);
                if (!this.levelSoundPlaying) {
                    Beheaded.backgroundCharacter.fadeLevelSoundOnBigWin(this.currentLevel, false);
                    Beheaded.soundPlayer.fadeSound(BeheadedSound.MAIN_GAME_MUSIC, 0, 1, 200);
                    if(this.swirlPlayed)
                        Beheaded.soundPlayer.fadeSound(SoundConfig.WILD_FIRE_LOOP, 0, SoulWild.volumeFire, 200);
                    this.levelSoundPlaying = true;
                }
            });
            this._bigWinStates.set(BigWinStates.STOP);
        } else {
            this.timeline.add(() => {
                Beheaded.poster.playTransition();
            }, '-=' + Beheaded.poster.transitionDuration);
        }

        function reachedLastLevel(level: number) {
            return level >= 4;
        }

        Logger.logDev('BigWin SoundDuration: ' + this.timeline.duration());

        this._currLevel++;
    }

    private postWin(): void {
        const stopDurr: number = 0.75;

        this.timeline.add(Beheaded.counter.bounceHit(8, this._bounceDuration), 0);

        SoundManager.stopBigWin();
        Beheaded.poster.stop(1.5);

        this.timeline.add(
            [
                () => {
                    SoundManager.fadeInAmbient(4000, 1);
                    Beheaded.parti.triggerFlames(false);
                    if (this._currLevel < 5) Beheaded.counter.moveToReset(0.8);
                    
                },
            ],
            this._bounceDuration + 0.15
        );
        if (!Beheaded.apiPlugin.gameClientConfiguration.belowStakeWinRestriction) {
            this.timeline.add(
                [
                    () => {
                        Beheaded.counter.showWays(1);
                    },
                ],
                1.1
            );
        }

        this.timeline.add(
            [
                () => {
                    Beheaded.counter.stop(this._winData!.singleWin, 1, 0.25, true);
                },
            ],
            2
        );
        this.timeline.add(() => SlotGame.keypadPlugin.setWin(this._winData!.totalWin, this.isTotalWin, this.isBelowStake));
        this.timeline.to(this, { duration: stopDurr });

        this._bigWinStates.set(BigWinStates.DONE);
    }

    private playBigWinCountUp(level: number, duration: number): gsap.core.Timeline {
        const fromValue: number = this.betValueRatio(level);
        let toValue: number = this.nextValueRatio(level);

        return Beheaded.counter.playBigWin(fromValue, toValue, duration, this._winData.allBetWayWinData.totalWinCombination);
    }

    public skip() {
        super.skip();
    }

    public done(): void {
        Beheaded.soundPlayer.stopSound(BeheadedSound.BIG_WIN[4]);
    }

    private betValueRatio(level: number): number {
        return (
            this._winData!.playedBetValue *
            this.targetRatio[Math.min(level, this.targetRatio.length - 1)].ratio
        );
    }

    private nextValueRatio(level: number): number {
        return level + 1 > this._winLevelTarget ? this._winData!.singleWin : this.betValueRatio(level + 1);
    }
}
